import checkIcon from '@/components/layout/icons/check.svg';
import type { AppSelectorInfo } from '@/components/layout/sidebar-types';
import { LOANS_SIDEBAR_STORAGE_KEY } from '@/components/layout/useSidebarShowSubItems';
import { LOANS_APP_ROLE_QUERY_KEY, resolveCurrentAppRole } from './app-role';
import { getExternalAppUrl, getLoansAppUrl } from './runtime-urls';
import loansIcon from '@/assets/loans-icon.svg';
import academyIcon from '@/assets/logo/academy-icon.svg';
import complianceIcon from '@/assets/logo/compliance-icon.svg';
import srIcon from '@/assets/sr-icon.svg';
import type { AppRole, LoansAppRole, User } from '@/types';

const LOANS_SIDEBAR_SCROLL_STORAGE_KEY = 'loans-sidebar-scroll-top';

type AppRoleMeta = {
  appName: string;
  roleName: string;
  icon: string;
  buildUrl: (role: AppRole) => string;
};

function trimTrailingSlash(url: string) {
  return url.replace(/\/+$/, '');
}

function buildUrlWithRole(rawUrl: string | undefined, role: AppRole) {
  const url = new URL(trimTrailingSlash(getExternalAppUrl(rawUrl)));
  url.searchParams.set(LOANS_APP_ROLE_QUERY_KEY, role);
  return url.toString();
}

function buildLoansUrl(role: LoansAppRole) {
  const baseUrl = getLoansAppUrl();
  const url = new URL(trimTrailingSlash(baseUrl));
  url.searchParams.set(LOANS_APP_ROLE_QUERY_KEY, role);
  return url.toString();
}

function buildPlainUrl(rawUrl: string | undefined) {
  return (role: AppRole) => buildUrlWithRole(rawUrl, role);
}

function buildAcademyUrl() {
  const academyBaseUrl = trimTrailingSlash(getExternalAppUrl(import.meta.env.VITE_ACADEMY_URL));
  const academyCallbackUrl = encodeURIComponent('/');
  return `${academyBaseUrl}/api/auth/keycloak-start?callbackUrl=${academyCallbackUrl}`;
}

const APP_ROLE_META: Record<AppRole, AppRoleMeta> = {
  sr_admin: {
    appName: 'Share Registry',
    roleName: 'Admin',
    icon: srIcon,
    buildUrl: buildPlainUrl(import.meta.env.VITE_SR_URL),
  },
  sr_issuer_admin: {
    appName: 'Share Registry',
    roleName: 'Issuer admin',
    icon: srIcon,
    buildUrl: buildPlainUrl(import.meta.env.VITE_SR_URL),
  },
  sr_issuer: {
    appName: 'Share Registry',
    roleName: 'Issuer',
    icon: srIcon,
    buildUrl: buildPlainUrl(import.meta.env.VITE_SR_URL),
  },
  sr_investor: {
    appName: 'Share Registry',
    roleName: 'Investor',
    icon: srIcon,
    buildUrl: buildPlainUrl(import.meta.env.VITE_SR_URL),
  },
  compliance_admin: {
    appName: 'Compliance',
    roleName: 'Admin',
    icon: complianceIcon,
    buildUrl: buildPlainUrl(import.meta.env.VITE_COMPLIANCE_URL),
  },
  compliance_user: {
    appName: 'Compliance',
    roleName: 'User',
    icon: complianceIcon,
    buildUrl: buildPlainUrl(import.meta.env.VITE_COMPLIANCE_URL),
  },
  loans_admin: {
    appName: 'Loans',
    roleName: 'Admin',
    icon: loansIcon,
    buildUrl: () => buildLoansUrl('loans_admin'),
  },
  loans_user: {
    appName: 'Loans',
    roleName: 'User',
    icon: loansIcon,
    buildUrl: () => buildLoansUrl('loans_user'),
  },
  academy_user: {
    appName: 'Academy',
    roleName: 'Academy user',
    icon: academyIcon,
    buildUrl: buildAcademyUrl,
  },
  academy_organisation_admin: {
    appName: 'Academy',
    roleName: 'Academy organisation admin',
    icon: academyIcon,
    buildUrl: buildAcademyUrl,
  },
};

const APP_ROLE_ORDER: AppRole[] = [
  'sr_issuer_admin',
  'sr_issuer',
  'sr_investor',
  'sr_admin',
  'compliance_admin',
  'compliance_user',
  'loans_admin',
  'loans_user',
  'academy_user',
  'academy_organisation_admin',
];

export function getAppSelectorInfo(user: User | null | undefined): AppSelectorInfo | undefined {
  const roles = Array.from(
    new Set((user?.availableAppRoles ?? []).filter(role => role in APP_ROLE_META)),
  ) as AppRole[];
  if (!roles.length) {
    return undefined;
  }

  const activeRole = resolveCurrentAppRole(user);
  const sortedRoles = [...roles].sort(
    (left, right) => APP_ROLE_ORDER.indexOf(left) - APP_ROLE_ORDER.indexOf(right),
  );

  return {
    value: activeRole,
    items: sortedRoles.map(role => {
      const meta = APP_ROLE_META[role];
      const isActive = role === activeRole;

      return {
        value: role,
        className: isActive ? 'cursor-default hover:!bg-white' : 'cursor-pointer hover:bg-gray-50',
        icon: meta.icon,
        label: (
          <div className="flex w-full items-center justify-between gap-4">
            <div className={isActive ? 'text-gray-500' : ''}>
              <div>{meta.appName}</div>
              <div className="text-xs capitalize">{meta.roleName}</div>
            </div>
            {isActive && (
              <div>
                <img src={checkIcon} alt="active" />
              </div>
            )}
          </div>
        ),
        onClick: () => {
          if (isActive) {
            return;
          }
          localStorage.removeItem(LOANS_SIDEBAR_STORAGE_KEY);
          sessionStorage.removeItem(LOANS_SIDEBAR_SCROLL_STORAGE_KEY);
          const targetUrl = meta.buildUrl(role);
          window.location.assign(targetUrl);
        },
      };
    }),
  };
}
